import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const values = [
  { name: "Integrity", desc: "We act with honesty, transparency and professionalism in everything we do." },
  { name: "Expertise", desc: "With years of industry experience, we provide knowledgeable guidance you can trust." },
  { name: "Care", desc: "Every family and every helper deserves personalised attention and respect." },
  { name: "Commitment", desc: "Our support continues beyond placement because successful matches take ongoing care." },
  { name: "Excellence", desc: "We continually improve our processes to deliver the highest standard of service." },
];

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-6">{t("about.overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#0A1128]">{t("about.title1")} <span className="italic text-[#688A4E]">{t("about.title2")}</span>.</h1>
        </div>
        <div className="lg:col-span-7 space-y-6 text-lg text-gray-700 leading-relaxed pt-4">
          <p>{t("about.p1")}</p>
          <p>{t("about.p2")}</p>
          <p>{t("about.p3")}</p>
        </div>
      </section>

      <section className="w-full h-[420px] sm:h-[520px] overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/90hy5wks_Helper_2%20children.jpeg" alt="Happy Home helper with children" className="w-full h-full object-cover"/>
      </section>

      {/* Mission / Vision */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32 grid md:grid-cols-2 gap-12">
        <div className="border-l-2 border-[#688A4E] pl-8">
          <div className="overline mb-4">Our Mission</div>
          <h3 className="font-serif text-3xl text-[#0A1128] mb-6 leading-snug">Successful, long-term partnerships between families and helpers.</h3>
          <ul className="space-y-3 text-gray-700">
            <li>· Thoughtful and compatible matching</li>
            <li>· Professional, honest advice</li>
            <li>· Reliable and responsive customer support</li>
            <li>· A smooth, stress-free hiring experience</li>
          </ul>
        </div>
        <div className="border-l-2 border-[#0A1128] pl-8">
          <div className="overline mb-4">Our Vision</div>
          <h3 className="font-serif text-3xl text-[#0A1128] mb-6 leading-snug">Singapore's most trusted domestic helper agency.</h3>
          <p className="text-gray-700 leading-relaxed">Recognised for delivering exceptional service, meaningful matches, and lasting peace of mind for every family we serve.</p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Our Values</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight">Five principles that guide every match.</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
            {values.map((v, i) => (
              <div key={v.name} className="bg-white p-8 hover:bg-[#E8ECE1] transition">
                <div className="text-xs tracking-[0.28em] text-gray-400 mb-4">0{i+1}</div>
                <h4 className="font-serif text-2xl text-[#0A1128] mb-3">{v.name}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <div className="font-serif text-6xl text-[#0A1128]">2006</div>
            <div className="overline mt-3">Serving Since</div>
          </div>
          <div>
            <div className="font-serif text-6xl text-[#688A4E]">4.3<span className="text-3xl">★</span></div>
            <div className="overline mt-3">Google Rating · 322+ Reviews</div>
          </div>
          <div>
            <div className="font-serif text-6xl text-[#0A1128]">20+</div>
            <div className="overline mt-3">Years Team Experience</div>
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center">
          <p className="font-serif italic text-2xl sm:text-3xl leading-relaxed mb-8">"We don't just place helpers — we help build happy homes."</p>
          <Link to="/contact" className="btn-outline-light" data-testid="about-contact-cta">Speak to a Consultant <ArrowRight className="w-4 h-4"/></Link>
        </div>
      </section>
    </div>
  );
}
